import type { GridShapesMenu, Shape } from '~/types/grid.types'

/**
 * Shape IDs for 45 degree grid shapes
 */
export enum SHAPE_IDS {
  // Basic Shapes
  LARGE_SQUARE = 'LargeSquare45deg',
  SQUARE = 'Square45deg',
  QUARTER = 'Quarter45deg',
  PIXEL = 'Pixel45deg',
  BIT = 'Bit45deg',

  // Halflap Shapes
  LARGE_HALF_LAP_VERTICAL = 'LargeHalflapVertical45deg',
  LARGE_HALF_LAP_HORIZONTAL = 'LargeHalflapHorizontal45deg',
  HALF_LAP_VERTICAL = 'HalflapVertical45deg',
  HALF_LAP_HORIZONTAL = 'HalflapHorizontal45deg',

  // Shiplap Shapes
  SHIPLAP_VERTICAL = 'ShiplapVertical45deg',
  SHIPLAP_HORIZONTAL = 'ShiplapHorizontal45deg',
  MINI_SHIPLAP_VERTICAL = 'MiniShiplapVertical45deg',
  MINI_SHIPLAP_HORIZONTAL = 'MiniShiplapHorizontal45deg',

  // Triangle Shapes
  LEFT_BOTTOM_TRIANGLE = 'LeftBottomTriangle45deg',
  RIGHT_TOP_TRIANGLE = 'RightTopTriangle45deg',
  LEFT_TOP_TRIANGLE = 'LeftTopTriangle45deg',
  RIGHT_BOTTOM_TRIANGLE = 'RightBottomTriangle45deg',
  SMALL_90_LEFT_BOTTOM_TRIANGLE = 'Small90LeftBottomTriangle45deg',
  SMALL_90_RIGHT_TOP_TRIANGLE = 'Small90RightTopTriangle45deg',
  SMALL_90_LEFT_TOP_TRIANGLE = 'Small90LeftTopTriangle45deg',
  SMALL_90_RIGHT_BOTTOM_TRIANGLE = 'Small90RightBottomTriangle45deg',

  // Large Half Circle Shapes
  LARGE_HALF_CIRCLE_VERTICAL = 'LargeHalfCircleVertical45deg',
  LARGE_HALF_CIRCLE_HORIZONTAL = 'LargeHalfCircleHorizontal45deg',
  LARGE_HALF_CIRCLE_VERTICAL_FLIPPED = 'LargeHalfCircleVerticalFlipped45deg',
  LARGE_HALF_CIRCLE_HORIZONTAL_FLIPPED = 'LargeHalfCircleHorizontalFlipped45deg',

  // Quarter Circle Shapes
  QUARTER_CIRCLE_BOTTOM_LEFT = 'QuarterCircleBottomLeft45deg',
  QUARTER_CIRCLE_TOP_LEFT = 'QuarterCircleTopLeft45deg',
  QUARTER_CIRCLE_BOTTOM_RIGHT = 'QuarterCircleBottomRight45deg',
  QUARTER_CIRCLE_TOP_RIGHT = 'QuarterCircleTopRight45deg',

  // Half Circle Shapes
  HALF_CIRCLE_VERTICAL = 'HalfCircleVertical45deg',
  HALF_CIRCLE_HORIZONTAL = 'HalfCircleHorizontal45deg',
  HALF_CIRCLE_VERTICAL_FLIPPED = 'HalfCircleVerticalFlipped45deg',
  HALF_CIRCLE_HORIZONTAL_FLIPPED = 'HalfCircleHorizontalFlipped45deg',

  // Small half-circle family present in Figma node 2850:163599 (2827:145313).
  SMALL_HALF_CIRCLE_VERTICAL = 'SmallHalfCircleVertical45deg',
  SMALL_HALF_CIRCLE_HORIZONTAL = 'SmallHalfCircleHorizontal45deg',
  SMALL_HALF_CIRCLE_VERTICAL_FLIPPED = 'SmallHalfCircleVerticalFlipped45deg',
  SMALL_HALF_CIRCLE_HORIZONTAL_FLIPPED = 'SmallHalfCircleHorizontalFlipped45deg',

  // Peak tile catalog entries — present in the Figma "Shapes available on
  // other grids" disabled row on the 45-degree panel as upright reference
  // icons. They can't actually be placed on a 45-degree grid, so they only
  // render as disabled markers via the panel contract.
  SMALL_PEAK = 'SmallPeak45deg',
  LARGE_CENTER_PEAK = 'LargeCenterPeak45deg',
  LARGE_UPPER_PEAK = 'LargeUpperPeak45deg',

  // Engraved 1' x 1' Shapes
  ENGRAVED_BACK_SLASH = 'EngravedBackSlash45deg',
  ENGRAVED_FORWARD_SLASH = 'EngravedForwardSlash45deg',
  ENGRAVED_CROSS = 'EngravedCross45deg',
  ENGRAVED_FOUR_SQUARE = 'EngravedFourSquare45deg',
  ENGRAVED_PARALLEL = 'EngravedParallel45deg',
  ENGRAVED_PARALLEL_VERTICAL = 'EngravedParallelVertical45deg',

  // Engraved 2' x 2' Shapes
  ENGRAVED_TANGENT = 'EngravedTangent45deg',
  ENGRAVED_TANGENT_90 = 'EngravedTangent9045deg',
  ENGRAVED_TANGENT_180 = 'EngravedTangent18045deg',
  ENGRAVED_TANGENT_270 = 'EngravedTangent27045deg',

  ENGRAVED_CRISS_CROSS = 'EngravedCrissCross45deg',
  ENGRAVED_CRISS_CROSS_90 = 'EngravedCrissCross9045deg',
  ENGRAVED_CRISS_CROSS_180 = 'EngravedCrissCross18045deg',
  ENGRAVED_CRISS_CROSS_270 = 'EngravedCrissCross27045deg',

  ENGRAVED_KNOT = 'EngravedKnot45deg',

  ENGRAVED_CONVERGE = 'EngravedConverge45deg',
  ENGRAVED_CONVERGE_90 = 'EngravedConverge9045deg',
  ENGRAVED_CONVERGE_180 = 'EngravedConverge18045deg',
  ENGRAVED_CONVERGE_270 = 'EngravedConverge27045deg',

  ENGRAVED_OFFSET = 'EngravedOffset45deg',
  ENGRAVED_OFFSET_90 = 'EngravedOffset9045deg',
  ENGRAVED_OFFSET_180 = 'EngravedOffset18045deg',
  ENGRAVED_OFFSET_270 = 'EngravedOffset27045deg',

  ENGRAVED_TOPO = 'EngravedTopo45deg',
  ENGRAVED_TOPO_90 = 'EngravedTopo9045deg',
  ENGRAVED_TOPO_180 = 'EngravedTopo18045deg',
  ENGRAVED_TOPO_270 = 'EngravedTopo27045deg',

  ENGRAVED_ARC = 'EngravedArc45deg',
  ENGRAVED_ARC_90 = 'EngravedArc9045deg',

  // new family
  ENGRAVED_LARGE_PARALLEL = 'EngravedLargeParallel45deg',
  ENGRAVED_LARGE_PARALLEL_90 = 'EngravedLargeParallel9045deg',
  ENGRAVED_RIVER = 'EngravedRiver45deg',
  ENGRAVED_RIVER_90 = 'EngravedRiver9045deg',
  ENGRAVED_SUBWAY = 'EngravedSubway45deg',
  ENGRAVED_SUBWAY_90 = 'EngravedSubway9045deg',
  ENGRAVED_SUBWAY_180 = 'EngravedSubway18045deg',
  ENGRAVED_SUBWAY_270 = 'EngravedSubway27045deg',
  ENGRAVED_ZEN = 'EngravedZen45deg',
  ENGRAVED_ZEN_90 = 'EngravedZen9045deg',
  ENGRAVED_ZEN_180 = 'EngravedZen18045deg',
  ENGRAVED_ZEN_270 = 'EngravedZen27045deg',
}

/**
 * Triangle modifier map for 45 degree grid
 */
export const fourtyFiveTriangleModifierMap: Record<string, string> = {
  'BottomLeft': SHAPE_IDS.LEFT_BOTTOM_TRIANGLE,
  'BottomRight': SHAPE_IDS.RIGHT_BOTTOM_TRIANGLE,
  'TopLeft': SHAPE_IDS.LEFT_TOP_TRIANGLE,
  'TopRight': SHAPE_IDS.RIGHT_TOP_TRIANGLE
};

/**
 * Helper function to find a shape by ID
 */
export const findShapeById = (id: string): Shape | undefined => {
  // Helper function to search in an array of shapes
  const searchInShapes = (shapes: Shape[]): Shape | undefined => {
    for (const shape of shapes) {
      // Check if current shape matches the ID
      if (shape.id === id) {
        return shape;
      }

      // If this shape has children, search in them too
      if (shape.children?.length) {
        const foundInChildren = searchInShapes(shape.children);
        if (foundInChildren) {
          return foundInChildren;
        }
      }
    }

    return undefined;
  };

  return searchInShapes(SHAPES);
};

export const SHAPES: Shape[] = [
  {
    type: 'rect',
    id: SHAPE_IDS.LARGE_SQUARE,
    icon: 'LargeSquare45deg',
    prefix: 'LargeSquare',
    label: 'Large Square',
    w: 240,
    h: 240,
    rotation: 45,
  },
  {
    type: 'rect',
    id: SHAPE_IDS.SQUARE,
    icon: 'Square45deg',
    prefix: 'Blank',
    label: 'Square',
    w: 120,
    h: 120,
    rotation: 45,
  },
  {
    type: 'rect',
    id: SHAPE_IDS.QUARTER,
    icon: 'Quarter45deg',
    prefix: 'Quarter',
    label: 'Quarter',
    w: 60,
    h: 60,
    rotation: 45,
  },
  {
    type: 'rect',
    id: SHAPE_IDS.PIXEL,
    icon: 'Pixel45deg',
    prefix: 'Pixel',
    label: 'Pixel',
    w: 30,
    h: 30,
    rotation: 45,
  },
  {
    type: 'rect',
    id: SHAPE_IDS.BIT,
    icon: 'Bit45deg',
    prefix: 'Bit',
    label: 'Bit',
    w: 20,
    h: 20,
    rotation: 45,
    admin: true,
  },
  {
    type: 'rect',
    id: SHAPE_IDS.LARGE_HALF_LAP_VERTICAL,
    icon: 'LargeHalflapVertical45deg',
    prefix: 'LargeHalflap',
    label: 'Large Halflap',
    w: 120,
    h: 240,
    rotation: 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.LARGE_HALF_LAP_HORIZONTAL,
        icon: 'LargeHalflapHorizontal45deg',
        prefix: 'LargeHalflap',
        label: 'Large Halflap Horizontal',
        w: 240,
        h: 120,
        rotation: 45,
      },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.HALF_LAP_VERTICAL,
    icon: 'HalflapVertical45deg',
    prefix: 'Half',
    label: 'Halflap',
    w: 60,
    h: 120,
    rotation: 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.HALF_LAP_HORIZONTAL,
        icon: 'HalflapHorizontal45deg',
        prefix: 'Half',
        label: 'Halflap Horizontal',
        w: 120,
        h: 60,
        rotation: 45,
      },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.SHIPLAP_VERTICAL,
    icon: 'ShiplapHorizontal45deg',
    prefix: 'Shiplap',
    label: 'Shiplap',
    w: 240,
    h: 60,
    rotation: 90 + 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.SHIPLAP_HORIZONTAL,
        icon: 'ShiplapHorizontal45deg',
        prefix: 'Shiplap',
        label: 'Shiplap Horizontal',
        w: 240,
        h: 60,
        rotation: 45,
      },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.MINI_SHIPLAP_VERTICAL,
    icon: 'MiniShiplapHorizontal45deg',
    prefix: 'MiniShiplap',
    label: 'Mini Shiplap',
    w: 120,
    h: 30,
    rotation: 90 + 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.MINI_SHIPLAP_HORIZONTAL,
        icon: 'MiniShiplapHorizontal45deg',
        prefix: 'MiniShiplap',
        label: 'Mini Shiplap Horizontal',
        w: 120,
        h: 30,
        rotation: 45,
      },
    ],
  },
  {
    type: 'rect-triangle',
    id: SHAPE_IDS.LEFT_BOTTOM_TRIANGLE,
    icon: 'RightTopTriangle45deg',
    prefix: 'Triangle',
    label: 'Triangle',
    rotation: 180 + 45,
    w: 120,
    h: 120,
    menuAlign: 'right',
    children: [
      {
        type: 'rect-triangle',
        id: SHAPE_IDS.RIGHT_TOP_TRIANGLE,
        icon: 'RightTopTriangle45deg',
        prefix: 'Triangle',
        label: 'Right Top Triangle',
        rotation: 0 + 45,
        w: 120,
        h: 120,
      },
      {
        type: 'rect-triangle',
        id: SHAPE_IDS.LEFT_TOP_TRIANGLE,
        icon: 'RightTopTriangle45deg',
        prefix: 'Triangle',
        label: 'Left Top Triangle',
        rotation: 270 + 45,
        w: 120,
        h: 120,
      },
      {
        type: 'rect-triangle',
        id: SHAPE_IDS.RIGHT_BOTTOM_TRIANGLE,
        icon: 'RightTopTriangle45deg',
        prefix: 'Triangle',
        label: 'Right Bottom Triangle',
        rotation: 90 + 45,
        w: 120,
        h: 120,
      },
    ],
  },
  {
    type: 'rect-triangle',
    id: SHAPE_IDS.SMALL_90_LEFT_BOTTOM_TRIANGLE,
    icon: 'Small90Triangle45deg',
    prefix: 'Small90Triangle',
    imgRotation: 270,
    label: 'Small 90 Triangle',
    rotation: 180 + 45,
    w: 60,
    h: 60,
    menuAlign: 'right',
    children: [
      {
        type: 'rect-triangle',
        id: SHAPE_IDS.SMALL_90_RIGHT_TOP_TRIANGLE,
        icon: 'Small90Triangle45deg',
        prefix: 'Small90Triangle',
        imgRotation: 270,
        label: 'Small 90 Right Top Triangle',
        rotation: 0 + 45,
        w: 60,
        h: 60,
      },
      {
        type: 'rect-triangle',
        id: SHAPE_IDS.SMALL_90_LEFT_TOP_TRIANGLE,
        icon: 'Small90Triangle45deg',
        prefix: 'Small90Triangle',
        imgRotation: 270,
        label: 'Small 90 Left Top Triangle',
        rotation: 270 + 45,
        w: 60,
        h: 60,
      },
      {
        type: 'rect-triangle',
        id: SHAPE_IDS.SMALL_90_RIGHT_BOTTOM_TRIANGLE,
        icon: 'Small90Triangle45deg',
        prefix: 'Small90Triangle',
        imgRotation: 270,
        label: 'Small 90 Right Bottom Triangle',
        rotation: 90 + 45,
        w: 60,
        h: 60,
      },
    ],
  },
  {
    type: 'arc',
    id: SHAPE_IDS.LARGE_HALF_CIRCLE_VERTICAL,
    icon: 'LargeHalfCircleHorizontalFlipped45deg',
    prefix: 'LargeHalfCircle',
    label: 'Large Half Circle',
    angle: 180,
    rotation: 90 + 45,
    imgRotation: 180 + 45,
    radius: 120,
    w: 120,
    h: 240,
    children: [
      {
        type: 'arc',
        id: SHAPE_IDS.LARGE_HALF_CIRCLE_HORIZONTAL,
        icon: 'LargeHalfCircleHorizontalFlipped45deg',
        prefix: 'LargeHalfCircle',
        label: 'Large Half Circle Horizontal',
        angle: 180,
        rotation: 180 + 45,
        imgRotation: 180 + 45,
        radius: 120,
        w: 240,
        h: 120,
      },
      {
        type: 'arc',
        id: SHAPE_IDS.LARGE_HALF_CIRCLE_VERTICAL_FLIPPED,
        icon: 'LargeHalfCircleHorizontalFlipped45deg',
        prefix: 'LargeHalfCircle',
        label: 'Large Half Circle Vertical Flipped',
        angle: 180,
        rotation: 270 + 45,
        imgRotation: 180 + 45,
        radius: 120,
        w: 120,
        h: 240,
      },
      {
        type: 'arc',
        id: SHAPE_IDS.LARGE_HALF_CIRCLE_HORIZONTAL_FLIPPED,
        icon: 'LargeHalfCircleHorizontalFlipped45deg',
        prefix: 'LargeHalfCircle',
        label: 'Large Half Circle Horizontal Flipped',
        angle: 180,
        rotation: 0 + 45,
        imgRotation: 180 + 45,
        radius: 120,
        w: 240,
        h: 120,
      },
    ],
  },
  {
    type: 'arc',
    id: SHAPE_IDS.QUARTER_CIRCLE_BOTTOM_LEFT,
    icon: 'QuarterCircleBottomRight45deg',
    prefix: 'QuarterCircle',
    label: 'Quarter Circle',
    angle: 90,
    rotation: 90 + 45,
    imgRotation: 180 + 45,
    radius: 120,
    w: 120,
    h: 120,
    children: [
      {
        type: 'arc',
        id: SHAPE_IDS.QUARTER_CIRCLE_TOP_LEFT,
        icon: 'QuarterCircleBottomRight45deg',
        prefix: 'QuarterCircle',
        label: 'Quarter Circle Top Left',
        angle: 90,
        rotation: 180 + 45,
        imgRotation: 180 + 45,
        radius: 120,
        w: 120,
        h: 120,
      },
      {
        type: 'arc',
        id: SHAPE_IDS.QUARTER_CIRCLE_BOTTOM_RIGHT,
        icon: 'QuarterCircleBottomRight45deg',
        prefix: 'QuarterCircle',
        label: 'Quarter Circle Bottom Right',
        angle: 90,
        rotation: 0 + 45,
        imgRotation: 180 + 45,
        radius: 120,
        w: 120,
        h: 120,
      },
      {
        type: 'arc',
        id: SHAPE_IDS.QUARTER_CIRCLE_TOP_RIGHT,
        icon: 'QuarterCircleBottomRight45deg',
        prefix: 'QuarterCircle',
        label: 'Quarter Circle Top Right',
        angle: 90,
        rotation: 270 + 45,
        imgRotation: 180 + 45,
        radius: 120,
        w: 120,
        h: 120,
      },
    ],
  },
  {
    type: 'arc',
    id: SHAPE_IDS.HALF_CIRCLE_VERTICAL,
    icon: 'HalfCircleHorizontalFlipped45deg',
    prefix: 'HalfCircle',
    label: 'Half Circle',
    angle: 180,
    rotation: 90 + 45,
    imgRotation: 180 + 45,
    radius: 60,
    w: 60,
    h: 120,
    children: [
      {
        type: 'arc',
        id: SHAPE_IDS.HALF_CIRCLE_HORIZONTAL,
        icon: 'HalfCircleHorizontalFlipped45deg',
        prefix: 'HalfCircle',
        label: 'Half Circle Horizontal',
        angle: 180,
        rotation: 180 + 45,
        imgRotation: 180 + 45,
        radius: 60,
        w: 120,
        h: 60,
      },
      {
        type: 'arc',
        id: SHAPE_IDS.HALF_CIRCLE_VERTICAL_FLIPPED,
        icon: 'HalfCircleHorizontalFlipped45deg',
        prefix: 'HalfCircle',
        label: 'Half Circle Vertical Flipped',
        angle: 180,
        rotation: 270 + 45,
        imgRotation: 180 + 45,
        radius: 60,
        w: 60,
        h: 120,
      },
      {
        type: 'arc',
        id: SHAPE_IDS.HALF_CIRCLE_HORIZONTAL_FLIPPED,
        icon: 'HalfCircleHorizontalFlipped45deg',
        prefix: 'HalfCircle',
        label: 'Half Circle Horizontal Flipped',
        angle: 180,
        rotation: 0 + 45,
        imgRotation: 180 + 45,
        radius: 60,
        w: 120,
        h: 60,
      },
    ],
  },
  {
    type: 'arc',
    id: SHAPE_IDS.SMALL_HALF_CIRCLE_VERTICAL,
    icon: 'HalfCircleHorizontalFlipped45deg',
    prefix: 'SmallHalfCircle',
    label: 'Small Half Circle',
    angle: 180,
    rotation: 90 + 45,
    imgRotation: 180 + 45,
    radius: 30,
    w: 30,
    h: 60,
    children: [
      { type: 'arc', id: SHAPE_IDS.SMALL_HALF_CIRCLE_HORIZONTAL, icon: 'HalfCircleHorizontalFlipped45deg', prefix: 'SmallHalfCircle', label: 'Small Half Circle Horizontal', angle: 180, rotation: 180 + 45, imgRotation: 180 + 45, radius: 30, w: 60, h: 30 },
      { type: 'arc', id: SHAPE_IDS.SMALL_HALF_CIRCLE_VERTICAL_FLIPPED, icon: 'HalfCircleHorizontalFlipped45deg', prefix: 'SmallHalfCircle', label: 'Small Half Circle Vertical Flipped', angle: 180, rotation: 270 + 45, imgRotation: 180 + 45, radius: 30, w: 30, h: 60 },
      { type: 'arc', id: SHAPE_IDS.SMALL_HALF_CIRCLE_HORIZONTAL_FLIPPED, icon: 'HalfCircleHorizontalFlipped45deg', prefix: 'SmallHalfCircle', label: 'Small Half Circle Horizontal Flipped', angle: 180, rotation: 0 + 45, imgRotation: 180 + 45, radius: 30, w: 60, h: 30 },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_BACK_SLASH,
    icon: 'EngravedBackSlash45deg',
    prefix: 'Slash',
    label: 'Slash',
    w: 120,
    h: 120,
    rotation: 0 + 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_FORWARD_SLASH,
        icon: 'EngravedBackSlash45deg',
        prefix: 'Slash',
        label: 'Forward Slash',
        w: 120,
        h: 120,
        rotation: 90 + 45,
      },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_CROSS,
    icon: 'EngravedCross45deg',
    prefix: 'Cross',
    label: 'Cross',
    w: 120,
    h: 120,
    rotation: 45,
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_FOUR_SQUARE,
    icon: 'EngravedFourSquare45deg',
    prefix: '4Square',
    label: 'Four Square',
    w: 120,
    h: 120,
    rotation: 45,
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_PARALLEL,
    icon: 'EngravedParallel45deg',
    prefix: 'Parallel',
    label: 'Parallel',
    w: 120,
    h: 120,
    rotation: 0 + 45,
    menuAlign: 'right',
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_PARALLEL_VERTICAL,
        icon: 'EngravedParallel45deg',
        prefix: 'Parallel',
        label: 'Parallel Vertical',
        w: 120,
        h: 120,
        rotation: 90 + 45,
      },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_TANGENT,
    icon: 'EngravedTangent45deg',
    prefix: 'Tangent',
    label: 'Tangent',
    w: 240,
    h: 240,
    rotation: 0 + 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_TANGENT_90,
        icon: 'EngravedTangent45deg',
        prefix: 'Tangent',
        label: 'Tangent 90',
        w: 240,
        h: 240,
        rotation: 90 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_TANGENT_180,
        icon: 'EngravedTangent45deg',
        prefix: 'Tangent',
        label: 'Tangent 180',
        w: 240,
        h: 240,
        rotation: 180 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_TANGENT_270,
        icon: 'EngravedTangent45deg',
        prefix: 'Tangent',
        label: 'Tangent 270',
        w: 240,
        h: 240,
        rotation: 270 + 45,
      },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_CRISS_CROSS_180,
    icon: 'EngravedCrissCross45deg',
    prefix: 'CrissCross',
    label: 'Criss Cross',
    w: 240,
    h: 240,
    rotation: 180 + 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_CRISS_CROSS_270,
        icon: 'EngravedCrissCross45deg',
        prefix: 'CrissCross',
        label: 'Criss Cross 270',
        w: 240,
        h: 240,
        rotation: 270 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_CRISS_CROSS,
        icon: 'EngravedCrissCross45deg',
        prefix: 'CrissCross',
        label: 'Criss Cross',
        w: 240,
        h: 240,
        rotation: 0 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_CRISS_CROSS_90,
        icon: 'EngravedCrissCross45deg',
        prefix: 'CrissCross',
        label: 'Criss Cross 90',
        w: 240,
        h: 240,
        rotation: 90 + 45,
      },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_CONVERGE,
    icon: 'EngravedConverge45deg',
    prefix: 'Converge',
    label: 'Converge',
    w: 240,
    h: 240,
    rotation: 0 + 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_CONVERGE_90,
        icon: 'EngravedConverge45deg',
        prefix: 'Converge',
        label: 'Converge 90',
        w: 240,
        h: 240,
        rotation: 90 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_CONVERGE_180,
        icon: 'EngravedConverge45deg',
        prefix: 'Converge',
        label: 'Converge 180',
        w: 240,
        h: 240,
        rotation: 180 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_CONVERGE_270,
        icon: 'EngravedConverge45deg',
        prefix: 'Converge',
        label: 'Converge 270',
        w: 240,
        h: 240,
        rotation: 270 + 45,
      },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_OFFSET,
    icon: 'EngravedOffset45deg',
    prefix: 'Offset',
    label: 'Offset',
    w: 240,
    h: 240,
    rotation: 0 + 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_OFFSET_90,
        icon: 'EngravedOffset45deg',
        prefix: 'Offset',
        label: 'Offset 90',
        w: 240,
        h: 240,
        rotation: 90 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_OFFSET_180,
        icon: 'EngravedOffset45deg',
        prefix: 'Offset',
        label: 'Offset 180',
        w: 240,
        h: 240,
        rotation: 180 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_OFFSET_270,
        icon: 'EngravedOffset45deg',
        prefix: 'Offset',
        label: 'Offset 270',
        w: 240,
        h: 240,
        rotation: 270 + 45,
      },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_TOPO,
    icon: 'EngravedTopo45deg',
    prefix: 'Topo',
    label: 'Topo',
    w: 240,
    h: 240,
    rotation: 0 + 45,
    menuAlign: 'right',
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_TOPO_90,
        icon: 'EngravedTopo45deg',
        prefix: 'Topo',
        label: 'Topo 90',
        w: 240,
        h: 240,
        rotation: 90 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_TOPO_180,
        icon: 'EngravedTopo45deg',
        prefix: 'Topo',
        label: 'Topo 180',
        w: 240,
        h: 240,
        rotation: 180 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_TOPO_270,
        icon: 'EngravedTopo45deg',
        prefix: 'Topo',
        label: 'Topo 270',
        w: 240,
        h: 240,
        rotation: 270 + 45,
      },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_ARC,
    icon: 'EngravedArc45deg',
    prefix: 'Arc',
    label: 'Arc',
    menuAlign: 'left',
    w: 240,
    h: 240,
    rotation: 0 + 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_ARC_90,
        icon: 'EngravedArc45deg',
        prefix: 'Arc',
        label: 'Arc 90',
        w: 240,
        h: 240,
        rotation: 90 + 45,
      },
    ],
  },
  //new
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_LARGE_PARALLEL,
    icon: 'EngravedLargeParallel45deg',
    prefix: 'LargeParallel',
    label: 'LargeParallel',
    w: 240,
    h: 240,
    rotation: 0 + 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_LARGE_PARALLEL_90,
        icon: 'EngravedLargeParallel45deg',
        prefix: 'LargeParallel',
        label: 'Large Parallel 90',
        w: 240,
        h: 240,
        rotation: 90 + 45,
      }
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_RIVER,
    icon: 'EngravedRiver45deg',
    prefix: 'River',
    label: 'River',
    w: 240,
    h: 240,
    rotation: 0 + 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_RIVER_90,
        icon: 'EngravedRiver45deg',
        prefix: 'River',
        label: 'River 90',
        w: 240,
        h: 240,
        rotation: 90 + 45,
      },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_SUBWAY,
    icon: 'EngravedSubway45deg',
    prefix: 'Subway',
    label: 'Subway',
    w: 240,
    h: 240,
    rotation: 0 + 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_SUBWAY_90,
        icon: 'EngravedSubway45deg',
        prefix: 'Subway',
        label: 'Subway 90',
        w: 240,
        h: 240,
        rotation: 90 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_SUBWAY_180,
        icon: 'EngravedSubway45deg',
        prefix: 'Subway',
        label: 'Subway 180',
        w: 240,
        h: 240,
        rotation: 180 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_SUBWAY_270,
        icon: 'EngravedSubway45deg',
        prefix: 'Subway',
        label: 'Subway 270',
        w: 240,
        h: 240,
        rotation: 270 + 45,
      },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_ZEN,
    icon: 'EngravedZen45deg',
    prefix: 'Zen',
    label: 'Zen',
    w: 240,
    h: 240,
    rotation: 0 + 45,
    children: [
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_ZEN_90,
        icon: 'EngravedZen45deg',
        prefix: 'Zen',
        label: 'Zen 90',
        w: 240,
        h: 240,
        rotation: 90 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_ZEN_180,
        icon: 'EngravedZen45deg',
        prefix: 'Zen',
        label: 'Zen 180',
        w: 240,
        h: 240,
        rotation: 180 + 45,
      },
      {
        type: 'rect',
        id: SHAPE_IDS.ENGRAVED_ZEN_270,
        icon: 'EngravedZen45deg',
        prefix: 'Zen',
        label: 'Zen 270',
        w: 240,
        h: 240,
        rotation: 270 + 45,
      },
    ],
  },
  {
    type: 'rect',
    id: SHAPE_IDS.ENGRAVED_KNOT,
    icon: 'EngravedKnot45deg',
    prefix: 'Knot',
    label: 'Knot',
    w: 240,
    h: 240,
    rotation: 45,
    admin: true,
  },
  // Peak tile silhouettes used as disabled markers on the 45-degree panel's
  // "Shapes available on other grids" row. The `icon` field reuses the
  // upright peak SVG (a 45°-specific SVG does not exist) so the picker
  // fallback chain in ShapePicker.vue resolves the visual; the `id` keeps
  // its 45deg suffix so it remains distinct from the selectable
  // SmallPeak/LargeCenterPeak/LargeUpperPeak entries on the square panel.
  {
    type: 'rect',
    id: SHAPE_IDS.SMALL_PEAK,
    icon: 'SmallPeak',
    prefix: 'SmallPeak',
    label: 'Small Peak',
    w: 60,
    h: 120,
    menuAlign: 'right',
    admin: true,
  },
  {
    type: 'rect',
    id: SHAPE_IDS.LARGE_CENTER_PEAK,
    icon: 'LargeCenterPeak',
    prefix: 'LargeCenterPeak',
    label: 'Large Center Peak',
    w: 60,
    h: 240,
    menuAlign: 'right',
    admin: true,
  },
  {
    type: 'rect',
    id: SHAPE_IDS.LARGE_UPPER_PEAK,
    icon: 'LargeUpperPeak',
    prefix: 'LargeUpperPeak',
    label: 'Large Upper Peak',
    w: 60,
    h: 240,
    menuAlign: 'right',
    admin: true,
  },
]

export const MENU: GridShapesMenu = {
  list: [
    {
      title: 'Shapes:',
      collapse: true,
      items: [
        ...SHAPES.filter((s: Shape) => [
          'LargeSquare45deg',
          'Square45deg',
          'Quarter45deg',
          'Pixel45deg',
          'Bit45deg',
        ].includes(s.id)),
        { breakpoint: true },
        ...SHAPES.filter((s: Shape) => [
          'LargeHalflapVertical45deg',
          'HalflapVertical45deg',
          'ShiplapVertical45deg',
          'MiniShiplapVertical45deg',
          'LeftBottomTriangle45deg',
          'Small90LeftBottomTriangle45deg',
        ].includes(s.id)),
        { breakpoint: true },
        ...SHAPES.filter((s: Shape) => [
          'LargeHalfCircleVertical45deg',
          'QuarterCircleBottomLeft45deg',
          'HalfCircleVertical45deg',
        ].includes(s.id)),
      ],
    },
    { title: 'Engraved 1\' x 1\' Tiles:',
      collapse: true,
      items: [
        ...SHAPES.filter((s: Shape) => [
          'EngravedBackSlash45deg',
          'EngravedCross45deg',
          'EngravedFourSquare45deg',
          'EngravedParallel45deg',
        ].includes(s.id)),
      ],
    },
    {
      title: 'Engraved 2\' x 2\' Tiles:',
      collapse: true,
      items: [
        { sectionSubtitle: 'Zen Garden Family:' },
        ...SHAPES.filter((s: Shape) => [
          'EngravedTangent45deg',
          'EngravedLargeParallel45deg',
          'EngravedZen45deg',
        ].includes(s.id)),
        { sectionSubtitle: 'Other Tiles:' },
        ...SHAPES.filter((s: Shape) => [
          'EngravedKnot45deg',
          'EngravedConverge45deg',
          'EngravedSubway45deg',
        ].includes(s.id)),
        { breakpoint: true },
        ...SHAPES.filter((s: Shape) => [
          'EngravedOffset45deg',
          'EngravedTopo45deg',
          'EngravedRiver45deg',
        ].includes(s.id)),
      ],
    },
  ],
  default: SHAPES.find((s: Shape) => 'Square45deg' === s.id),
}
